/**
 * Project Management
 *
 * Single source of truth for all project/session types, helpers, serialization,
 * and the migration system.
 * Consumed by as-app, db.ts, the store, and inspector components.
 *
 * === Migrations ===
 * App developers register version-keyed migrations with setMigrations().
 * applyMigrations() is called during project load and runs all migrations
 * between the saved version and the current version, in order.
 *
 * Two wildcard keys run on every load regardless of version: '*' runs before
 * all version-keyed migrations, '>*' runs after them.
 *
 * Each version entry can migrate any combination of:
 *   uiInputs   — flat Record<string, any>
 *   customData — flat Record<string, any>
 *   session    — flat SessionParams map keyed by "packageIdent:paramIdent"
 *
 * Each field accepts either a mapping object { oldKey: newKey, removed: null }
 * or a transform function (data) => data.
 *
 * The session structural merge (carrying old values into the current SPS
 * structure) runs after explicit migrations, so renamed/added params are
 * handled automatically. Explicit session migrations only need to cover
 * renames or value transforms.
 *
 * Usage:
 *   import { setMigrations } from 'as-components/data/projectManagement.ts';
 *
 *   setMigrations({
 *     '1.2.0': {
 *       uiInputs: { 'wall-thickness': 'wallThickness', 'legacyParam': null },
 *       session:  { 'main:oldParam': 'main:newParam' },
 *     },
 *     '2.0.0': {
 *       uiInputs:   (inputs) => ({ ...inputs, height: inputs.height / 10 }),
 *       customData: { 'legacyField': 'newField' },
 *     },
 *     '>*': {   // every load, after all version-keyed migrations
 *       session: (params) => { params['main:preview'] = false; return params; },
 *     },
 *   });
 */

import type { VPData } from './data-interfaces.internal.ts';
import { zipSync, unzipSync, strToU8, strFromU8 } from 'fflate';
import { liveAppData } from '../store/store.ts';

// ============================================================================
// Types
// ============================================================================

export type ProjectID = string;
export type AppID = string;

export interface ProjectMetadata {
  id: ProjectID;
  name: string;
  appId: AppID;
  appVersion?: string;   // package.json version at save time
  createdAt: Date;
  updatedAt: Date;
  savedAt?: Date;        // undefined = never saved
  authorId?: string;
}

/** An output mesh snapshot stored within a project */
export interface OutputMesh {
  meshId: string;
  meshName: string;
  stlData?: Blob;
}

export interface Project {
  metadata: ProjectMetadata;
  /**
   * All input state keyed by element ID.
   * Includes scalar values (numbers, strings, booleans) and file objects.
   */
  uiInputs: Record<string, any>;
  
  /** Paramate session backup zip (from saveSession) */
  paramateSession?: Blob;

  /** WIP /NOT USED Output mesh snapshots produced during the session */
  outputMeshes?: OutputMesh[];
  /**
   * App-defined data that is persisted with the project and restored
   * via the `session-restored` event when the project is loaded.
   * Also reflected in `currentProjectData.customData`.
   */
  customData?: Record<string, any>;

  /**
   * WIP / MAY DELETE File System Access API handle for the linked .asproject file.
   * Present when the user has chosen a local file location.
   * FileSystemFileHandle is structured-cloneable, so it survives IDB round-trips.
   * undefined = never linked to a local file ("undownloaded").
   */
  fileHandle?: FileSystemFileHandle;
  // outputMeshVPData?: VPData[];  // disabled - raw geometry snapshots, reserved for future use
}

// ============================================================================
// Runtime project data (mutable singleton — read/write directly from app code)
// ============================================================================

const _rawCustomData: Record<string, any> = {};
let _suppressCustomDataEvents = false;

const _customDataSubscribers = new Set<(data: Record<string, any>) => void>();
// Per-key subscribers, so apps can react to a single customData field (valtio
// subscribeKey-style) instead of diffing the whole object on every change. The
// proxy's set/delete traps already know which key was mutated, so we fan out by
// key. NOTE: top-level keys only — nested objects are treated as immutable
// values, so replace the whole object (customData.foo = {...}) to notify.
const _customDataKeySubscribers = new Map<string, Set<(value: any, key: string) => void>>();

function _notifyKeySubscribers(key: string): void {
  if (_suppressCustomDataEvents) return;
  const subs = _customDataKeySubscribers.get(key);
  if (!subs || subs.size === 0) return;
  const value = _rawCustomData[key];
  subs.forEach(cb => cb(value, key));
}

function _notifyCustomDataSubscribers(silent = false): void {
  if (_suppressCustomDataEvents) return;
  const snapshot = { ..._rawCustomData };
  // import { devlog } from '../logic/utils.ts'; // can't import here due to circular deps usually, use console.debug
  console.debug(`[CUSTOM-DATA] Notification: silent=${silent}, keys=[${Object.keys(snapshot)}]`);
  _customDataSubscribers.forEach(cb => cb(snapshot));

  window.dispatchEvent(new CustomEvent('as-custom-data-updated', {
    detail: { customData: snapshot, silent }
  }));

  if (!silent) {
    window.dispatchEvent(new CustomEvent('as-custom-data-changed', {
      detail: { customData: snapshot }
    }));
    window.dispatchEvent(new CustomEvent('as-project-change'));
  }
}

const _customDataProxy = new Proxy(_rawCustomData, {
  set(target, prop, value) {
    if (target[prop as string] === value) return true;
    target[prop as string] = value;
    _notifyKeySubscribers(prop as string);
    _notifyCustomDataSubscribers();
    return true;
  },
  deleteProperty(target, prop) {
    if (!(prop in target)) return true;
    delete target[prop as string];
    _notifyKeySubscribers(prop as string);
    _notifyCustomDataSubscribers();
    return true;
  }
});

export const currentProjectData: {
  info: Record<string, any>;
  /** Paramate configSession ID — written by as-configurator on init */
  sessionId: string;
  uiState: Record<string, any>;
  paramateParamCache: Record<string, any>;
  meshes: Record<string, VPData>;
  appDefined: Record<string, any>;
  
  
  uiInputs: Record<string, any>;
  /** Mirrors Project.customData for the currently active project */
  customData: Record<string, any>;


  /** Update customData. Set silent=true to skip history entry and dirty-marking. */
  setCustomData(v: Record<string, any>, silent?: boolean): void;
  /** Subscribe to customData changes. Returns unsubscribe function. */
  subscribe(cb: (data: Record<string, any>) => void): () => void;
  /**
   * Subscribe to changes of a single top-level customData key (valtio
   * subscribeKey-style). Does NOT fire immediately. Returns an unsubscribe fn.
   * Fires on direct `customData[key] = …`, on delete, and on `setCustomData`
   * when that key's value changes (e.g. a project restore).
   */
  subscribeKey(key: string, cb: (value: any, key: string) => void): () => void;
} = {
  info: {},
  sessionId: '',
  uiState: {},
  get uiInputs() { return liveAppData.uiInputs; },
  set uiInputs(v: Record<string, any>) { liveAppData.uiInputs = v; },
  paramateParamCache: {},
  meshes: {},
  appDefined: {},
  get customData() { return _customDataProxy; },
  set customData(v: Record<string, any>) {
    this.setCustomData(v, false);
  },
  setCustomData(v: Record<string, any>, silent = false) {
    // Capture which top-level keys actually change so per-key subscribers fire
    // exactly once each on a bulk replace (e.g. a project restore).
    const before = { ..._rawCustomData };
    _suppressCustomDataEvents = true;
    for (const k in _rawCustomData) delete (_rawCustomData as any)[k];
    Object.assign(_rawCustomData, v);
    _suppressCustomDataEvents = false;
    const changedKeys = new Set<string>([...Object.keys(before), ...Object.keys(_rawCustomData)]);
    for (const k of changedKeys) {
      if (before[k] !== _rawCustomData[k]) _notifyKeySubscribers(k);
    }
    _notifyCustomDataSubscribers(silent);
  },
  subscribe(cb) {
    _customDataSubscribers.add(cb);
    cb({ ..._rawCustomData });
    return () => _customDataSubscribers.delete(cb);
  },
  subscribeKey(key, cb) {
    let subs = _customDataKeySubscribers.get(key);
    if (!subs) { subs = new Set(); _customDataKeySubscribers.set(key, subs); }
    subs.add(cb);
    return () => { _customDataKeySubscribers.get(key)?.delete(cb); };
  }
};

/** Global helper to subscribe to customData changes */
export function subscribeToCustomData(cb: (data: Record<string, any>) => void): () => void {
  return currentProjectData.subscribe(cb);
}

window.asGlobals.currentProjectData = currentProjectData;

// ============================================================================
// Helpers
// ============================================================================

function generateProjectId(): ProjectID {
  return `proj-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Create a new project with default values.
 * @param appId      - The application ID this project belongs to
 * @param name       - Optional project name (defaults to "Untitled")
 * @param appVersion - Optional app version to embed in metadata
 */
export function createNewProject(appId: AppID, name?: string, appVersion?: string): Project {
  const now = new Date();
  return {
    metadata: {
      id: generateProjectId(),
      name: name?.trim() || 'Untitled',
      appId,
      ...(appVersion !== undefined && { appVersion }),
      createdAt: now,
      updatedAt: now,
      savedAt: undefined,
    },
    uiInputs: {},
    paramateSession: undefined,
    customData: {},
  };
}

/**
 * Return true if the project has unsaved changes.
 * (Never saved, or updated since last save.)
 */
export function isProjectUnsaved(project: Project): boolean {
  if (!project.metadata.savedAt) return true;
  return project.metadata.updatedAt > project.metadata.savedAt;
}

/**
 * Return true if the project has been exported to an .asproject file at least once.
 * False = "undownloaded" — only exists in IDB, never exported.
 *
 * Intentionally based on savedAt, not fileHandle — fileHandle is a Chrome/Edge
 * enhancement only; savedAt is set on every export path (file handle or download).
 */
export function isProjectDownloaded(project: Project): boolean {
  return !!project.metadata.savedAt;
}

/** Return true if the project still has the default "Untitled" name. */
export function isProjectUntitled(project: Project): boolean {
  return project.metadata.name === 'Untitled';
}

/** Return a copy of the project with savedAt set to now. */
export function markProjectSaved(project: Project): Project {
  return {
    ...project,
    metadata: { ...project.metadata, savedAt: new Date() },
  };
}

/** Return a copy of the project with updatedAt bumped to now. */
export function touchProject(project: Project): Project {
  return {
    ...project,
    metadata: { ...project.metadata, updatedAt: new Date() },
  };
}

// ============================================================================
// .asproject serialization (zip via fflate)
// ============================================================================

async function blobToU8(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Serialize a project to a zip Blob suitable for writing as an .asproject file.
 * Zip entries:
 *   project.json          — metadata, uiInputs, customData, outputMeshes index
 *   session.bin           — paramateSession (if present)
 *   meshes/<meshId>.stl   — each output mesh STL (if present)
 *
 * fileHandle is intentionally omitted from the zip.
 */
export async function serializeProject(project: Project): Promise<Blob> {
  const manifest: any = {
    version: 1,
    metadata: {
      ...project.metadata,
      createdAt: project.metadata.createdAt instanceof Date
        ? project.metadata.createdAt.toISOString()
        : project.metadata.createdAt,
      updatedAt: project.metadata.updatedAt instanceof Date
        ? project.metadata.updatedAt.toISOString()
        : project.metadata.updatedAt,
      savedAt: project.metadata.savedAt instanceof Date
        ? project.metadata.savedAt.toISOString()
        : (project.metadata.savedAt ?? null),
    },
    uiInputs: project.uiInputs,
    customData: project.customData ?? {},
    outputMeshes: (project.outputMeshes ?? []).map(m => ({
      meshId: m.meshId,
      meshName: m.meshName,
      hasStl: !!m.stlData,
    })),
    hasSession: !!project.paramateSession,
  };

  const files: Record<string, Uint8Array> = {
    'project.json': strToU8(JSON.stringify(manifest)),
  };

  if (project.paramateSession) {
    files['session.bin'] = await blobToU8(project.paramateSession);
  }

  for (const mesh of project.outputMeshes ?? []) {
    if (mesh.stlData) {
      files[`meshes/${mesh.meshId}.stl`] = await blobToU8(mesh.stlData);
    }
  }

  return new Blob([zipSync(files, { level: 6 })], { type: 'application/zip' });
}

/**
 * Deserialize an .asproject zip Blob back into a Project.
 * fileHandle will be undefined — caller must re-attach it.
 */
export async function deserializeProject(blob: Blob): Promise<Project> {
  const files = unzipSync(new Uint8Array(await blob.arrayBuffer()));

  const manifest = JSON.parse(strFromU8(files['project.json']));

  const metadata: ProjectMetadata = {
    ...manifest.metadata,
    name: manifest.metadata.name?.trim() || 'Untitled',
    createdAt: new Date(manifest.metadata.createdAt),
    updatedAt: new Date(manifest.metadata.updatedAt),
    savedAt: manifest.metadata.savedAt ? new Date(manifest.metadata.savedAt) : undefined,
  };

  const outputMeshes: OutputMesh[] = (manifest.outputMeshes ?? []).map((m: any) => ({
    meshId: m.meshId,
    meshName: m.meshName,
    stlData: files[`meshes/${m.meshId}.stl`]
      ? new Blob([files[`meshes/${m.meshId}.stl`]], { type: 'application/octet-stream' })
      : undefined,
  }));

  return {
    metadata,
    uiInputs: manifest.uiInputs ?? {},
    customData: manifest.customData ?? {},
    paramateSession: files['session.bin']
      ? new Blob([files['session.bin']], { type: 'application/zip' })
      : undefined,
    outputMeshes,
    fileHandle: undefined,
  };
}

/**
 * Wrap a bare Paramate session zip (a `parameters.json`-only export with no
 * `project.json` wrapper, e.g. an older custom save path or a hand-exported
 * session) into a fresh Project. The raw zip is kept as the `paramateSession`
 * so the normal load + migration pipeline can structural-merge it into the
 * current session structure. appVersion is intentionally left undefined so
 * every registered migration runs and applyMigrations performs the merge.
 *
 * This is deliberately NOT done automatically by deserializeProject — call it
 * explicitly when you know you're handling a bare session rather than an
 * `.asproject`.
 */
export function projectFromBareSession(blob: Blob): Project {
  const now = new Date();
  return {
    metadata: {
      id: generateProjectId(),
      name: 'Untitled',
      appId: liveAppData.appId ?? '',
      createdAt: now,
      updatedAt: now,
      savedAt: undefined,
    },
    uiInputs: {},
    customData: {},
    paramateSession: new Blob([blob], { type: 'application/zip' }),
    outputMeshes: [],
    fileHandle: undefined,
  };
}

// ============================================================================
// Migration types
// ============================================================================

/** Flat map of Paramate session params keyed by "packageIdent:paramIdent" */
export type SessionParams = Record<string, any>;

/** Rename/delete mapping: { oldKey: newKey } or { oldKey: null } to delete */
export type FieldMapping = Record<string, string | null>;

export type FieldTransform<T> = (data: T) => T;

export type MigrationField<T> = FieldMapping | FieldTransform<T>;

export interface MigrationEntry {
  uiInputs?:   MigrationField<Record<string, any>>;
  customData?: MigrationField<Record<string, any>>;
  session?:    MigrationField<SessionParams>;
}

/**
 * Version string -> migration entry.
 *
 * Two wildcard keys run on EVERY load, regardless of the saved/current version:
 *   '*'  — runs before all version-keyed migrations
 *   '>*' — runs after all version-keyed migrations
 * Use them for unconditional restore fix-ups (resetting transient params,
 * dropping keys so the current session's values win in the structural merge).
 */
export type MigrationMap = Record<string, MigrationEntry>;

/** Wildcard key: entry runs on every load, before all version-keyed migrations. */
export const WILDCARD_BEFORE = '*';
/** Wildcard key: entry runs on every load, after all version-keyed migrations. */
export const WILDCARD_AFTER = '>*';

/**
 * Session params that are never restored from a saved project — the current
 * (live) session's values always win the structural merge. TrCAD script
 * metadata reflects the running script build, not user state, so restoring a
 * stale saved value would misreport the version after load. Deleting a key
 * absent from a given app's session is a no-op.
 */
export const NEVER_RESTORE_SESSION_PARAMS = [
  'main:metadata:scriptVersion',
  'main:metadata:trcadVersion',
];

// ============================================================================
// Migration registry
// ============================================================================

let _migrations: MigrationMap = {};

export function setMigrations(migrations: MigrationMap): void {
  _migrations = migrations;
}

// ============================================================================
// Migration helpers (internal)
// ============================================================================

function compareVersions(a: string, b: string): number {
  const av = a.split('.').map(n => parseInt(n, 10));
  const bv = b.split('.').map(n => parseInt(n, 10));
  for (let i = 0; i < Math.max(av.length, bv.length); i++) {
    const diff = (av[i] ?? 0) - (bv[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function getApplicableMigrations(
  savedVersion: string | undefined,
  currentVersion: string
): [string, MigrationEntry][] {
  const entries = Object.entries(_migrations);
  const versioned = entries
    .filter(([version]) => version !== WILDCARD_BEFORE && version !== WILDCARD_AFTER)
    .filter(([version]) => {
      if (!savedVersion) return true; // no saved version — run all
      return compareVersions(version, savedVersion) > 0 &&
             compareVersions(version, currentVersion) <= 0;
    })
    .sort(([a], [b]) => compareVersions(a, b));
  return [
    ...entries.filter(([v]) => v === WILDCARD_BEFORE),
    ...versioned,
    ...entries.filter(([v]) => v === WILDCARD_AFTER),
  ];
}

function applyFieldMigration<T extends Record<string, any>>(
  data: T,
  migration: MigrationField<T>
): T {
  if (typeof migration === 'function') {
    return migration({ ...data });
  }
  const result: Record<string, any> = { ...data };
  for (const [oldKey, newKey] of Object.entries(migration)) {
    if (!(oldKey in result)) continue;
    if (newKey === null) {
      delete result[oldKey];
    } else {
      result[newKey] = result[oldKey];
      if (newKey !== oldKey) delete result[oldKey];
    }
  }
  return result as T;
}

/** Build a flat { "pkg:param": value } map from a parameters.json object */
function buildFlatParams(paramsJson: any): SessionParams {
  const flat: SessionParams = {};
  const packages = paramsJson?.objects?.[0]?.packages ?? [];
  for (const pkg of packages) {
    for (const param of [...(pkg.oparams ?? []), ...(pkg.pparams ?? [])]) {
      // Sanitize "undefined" strings that can appear in corrupt/old sessions —
      // mirrors the explicit check in merged-restore.js. Without this, numeric
      // params would reach TrCAD as NaN.
      const raw = param.value;
      flat[`${pkg.ident}:${param.ident}`] = String(raw).includes('undefined')
        ? (param.attributes?.value ?? raw)
        : raw;
    }
  }
  return flat;
}

/**
 * Build a flat { "pkg:param": changed } map from a parameters.json object.
 *
 * The saved `attributes.changed` flag is carried through restore verbatim rather
 * than overwritten. Forcing every restored param to changed=true makes TrCAD treat
 * untouched/derived params as fresh user edits, which fires conditional recompute
 * branches in scripts with coupled params (e.g. Stabilizers
 * `if (taperAngle.changed) bottomDiam = bottomDiamFromParams(...)`) and corrupts the
 * restored value. A saved session is an internally-consistent snapshot — restoring
 * it as "nothing changed" lets TrCAD re-derive coupled params from consistent values.
 */
function buildChangedMap(paramsJson: any): Record<string, boolean> {
  const changed: Record<string, boolean> = {};
  const packages = paramsJson?.objects?.[0]?.packages ?? [];
  for (const pkg of packages) {
    for (const param of [...(pkg.oparams ?? []), ...(pkg.pparams ?? [])]) {
      changed[`${pkg.ident}:${param.ident}`] = String(param.attributes?.changed) === 'true';
    }
  }
  return changed;
}

/** Write a flat param map back into a parameters.json structure (in-place clone) */
function applyFlatParamsToStructure(
  paramsJson: any,
  flat: SessionParams,
  changedMap: Record<string, boolean>
): any {
  const result = JSON.parse(JSON.stringify(paramsJson));
  const packages = result?.objects?.[0]?.packages ?? [];
  for (const pkg of packages) {
    for (const param of [...(pkg.oparams ?? []), ...(pkg.pparams ?? [])]) {
      const key = `${pkg.ident}:${param.ident}`;
      if (key in flat) {
        param.value = flat[key];
        // Preserve the saved changed flag (default false for params the migration
        // renamed/introduced). Never force true — see buildChangedMap.
        if (param.attributes) param.attributes.changed = changedMap[key] ?? false;
      }
    }
  }
  return result;
}

/**
 * Append params present in the old session but missing from the merged (new)
 * structure, matched into their package by ident.
 *
 * The structural merge target is the *current live* Paramate session, which only
 * contains params that are currently exposed. TrCAD scripts conditionally expose
 * params behind toggles (e.g. bottomHoleDiam/counterboreDiam behind addBottomHole/
 * addCounterbore), so when the configurator has been reset to defaults (toggles off)
 * those params are absent from the target — and applyFlatParamsToStructure silently
 * drops their saved values, re-defaulting them on restore. Carrying the old param
 * over (with its saved value + its saved changed flag) lets TrCAD honor the saved
 * value once the enabling toggle re-activates. Genuinely-removed params carried over
 * are harmless — TrCAD ignores unknown params on restore.
 */
function carryOverMissingParams(
  targetJson: any,
  oldJson: any,
  flat: SessionParams,
  changedMap: Record<string, boolean>
): void {
  const targetPkgs: any[] = targetJson?.objects?.[0]?.packages ?? [];
  const oldPkgs: any[] = oldJson?.objects?.[0]?.packages ?? [];
  const byIdent = new Map<string, any>(targetPkgs.map(p => [p.ident, p]));

  for (const oldPkg of oldPkgs) {
    const tgtPkg = byIdent.get(oldPkg.ident);
    if (!tgtPkg) continue; // package absent from current structure — out of scope
    const have = new Set<string>(
      [...(tgtPkg.oparams ?? []), ...(tgtPkg.pparams ?? [])].map((p: any) => p.ident)
    );
    for (const isPublic of [false, true]) {
      const oldList: any[] = (isPublic ? oldPkg.pparams : oldPkg.oparams) ?? [];
      const tgtList: any[] = isPublic ? (tgtPkg.pparams ??= []) : (tgtPkg.oparams ??= []);
      for (const oldParam of oldList) {
        if (have.has(oldParam.ident)) continue;
        const key = `${oldPkg.ident}:${oldParam.ident}`;
        // Respect migrations: a key renamed or deleted (null) in `flat` must NOT be
        // re-introduced under its old ident. Only carry over keys still in `flat`.
        if (!(key in flat)) continue;
        const clone = JSON.parse(JSON.stringify(oldParam));
        clone.value = flat[key];
        if (clone.attributes) clone.attributes.changed = changedMap[key] ?? false;
        tgtList.push(clone);
      }
    }
  }
}

/**
 * Core session processor:
 * 1. Unpack old session → flat param map
 * 2. Apply session migrations (mappings / transforms)
 * 3. If newStructureBlob provided: merge migrated values into new structure
 *    (structural merge — new params get defaults, removed params are dropped)
 *    Otherwise: write values back into old structure
 * 4. Repack and return new session blob
 */
async function processSession(
  oldSessionBlob: Blob,
  migrations: MigrationField<SessionParams>[],
  newStructureBlob?: Blob
): Promise<Blob> {
  const oldZip = unzipSync(new Uint8Array(await oldSessionBlob.arrayBuffer()));
  const oldParamsJson = JSON.parse(strFromU8(oldZip['parameters.json']));

  let flat = buildFlatParams(oldParamsJson);
  // Saved changed flags, keyed by pre-migration "pkg:ident". Carried through restore
  // verbatim; renamed/introduced params default to false (see buildChangedMap).
  const changedMap = buildChangedMap(oldParamsJson);

  for (const migration of migrations) {
    flat = applyFieldMigration(flat, migration);
  }

  // Script metadata always reflects the current build: absent from the flat map,
  // these keep the live structure's values in applyFlatParamsToStructure and are
  // not re-introduced by carryOverMissingParams.
  for (const key of NEVER_RESTORE_SESSION_PARAMS) delete flat[key];

  let targetParamsJson: any;
  let filesJson: Uint8Array;

  if (newStructureBlob) {
    const newZip = unzipSync(new Uint8Array(await newStructureBlob.arrayBuffer()));
    targetParamsJson = applyFlatParamsToStructure(
      JSON.parse(strFromU8(newZip['parameters.json'])),
      flat,
      changedMap
    );
    // Preserve saved params that are absent from the current (possibly toggle-reduced)
    // structure so conditionally-exposed TrCAD params don't re-default on restore.
    carryOverMissingParams(targetParamsJson, oldParamsJson, flat, changedMap);
    // Old files.json carries the uploaded-file index; old binary assets follow below
    filesJson = oldZip['files.json'] ?? newZip['files.json'];
  } else {
    targetParamsJson = applyFlatParamsToStructure(oldParamsJson, flat, changedMap);
    filesJson = oldZip['files.json'];
  }

  const outFiles: Record<string, Uint8Array> = {
    'parameters.json': strToU8(JSON.stringify(targetParamsJson)),
  };
  if (filesJson) outFiles['files.json'] = filesJson;

  // Carry over binary file assets from the old session
  for (const [name, data] of Object.entries(oldZip)) {
    if (name.startsWith('files/')) outFiles[name] = data as Uint8Array;
  }

  return new Blob([zipSync(outFiles, { level: 6 })], { type: 'application/zip' });
}

// ============================================================================
// applyMigrations — public API
// ============================================================================

/**
 * Apply all registered migrations between savedVersion and currentVersion.
 *
 * Pass currentSessionBlob (from configuratorEl.getSessionBlob()) to enable the
 * structural merge — old param values are carried into the new Paramate
 * structure after explicit session migrations run.
 *
 * Returns a new project object; the original is not mutated.
 */
export async function applyMigrations(
  project: Project,
  currentVersion: string,
  currentSessionBlob?: Blob
): Promise<Project> {
  const savedVersion = project.metadata.appVersion;
  const applicable = getApplicableMigrations(savedVersion, currentVersion);

  const needsSessionMigration = applicable.some(([, e]) => e.session);
  const needsFields = applicable.some(([, e]) => e.uiInputs || e.customData);
  const needsStructuralMerge = !!project.paramateSession && !!currentSessionBlob;

  if (!needsSessionMigration && !needsFields && !needsStructuralMerge) return project;

  let { uiInputs, customData, paramateSession } = project;

  for (const [, entry] of applicable) {
    if (entry.uiInputs)  uiInputs   = applyFieldMigration(uiInputs, entry.uiInputs);
    if (entry.customData && customData)
      customData = applyFieldMigration(customData, entry.customData);
  }

  if (paramateSession && (needsSessionMigration || needsStructuralMerge)) {
    const sessionMigrations = applicable
      .filter(([, e]) => e.session)
      .map(([, e]) => e.session!);
    paramateSession = await processSession(paramateSession, sessionMigrations, currentSessionBlob);
  }

  return { ...project, uiInputs, customData, paramateSession };
}
